import { StatsContexterServiceInterface } from '../../service/stats-contexter/service.interface';
import { StatsEmitterServiceInterface } from '../../service/stats-emitter/service.interface';
import { EventEmitterInterface } from '../event/emitter.interface';
import { StatsContextLocalInterface } from './context/local.interface';
import { StatsData } from './data';
import { StatsEmitterEntityStrategy } from './emitter/entity-strategy';
import { StatsDataEntityInterface } from './data/entity.interface';
import { StatsEmitterEvent } from './emitter.event';
import { StatsDebuggerServiceInterface } from '../../service/stats-debugger/service.interface';
import { StatsPlatformEnum } from './platform.enum';

export class StatsEmitter implements StatsEmitterServiceInterface {
  /**
   * Constructor
   */
  constructor(
    private eventEmitter: EventEmitterInterface,
    private statsContextService: StatsContexterServiceInterface,
    private statsEmitterEntityStrategy: StatsEmitterEntityStrategy,
    private statsDebuggerService: StatsDebuggerServiceInterface
  ) {}

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritDoc
   */
  public reset(): void {
    this.statsEmitterEntityStrategy.reset();
  }

  /**
   * @inheritDoc
   */
  public pageView(): void {
    const context = this.statsContextService.getContext();

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.pageView, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.pageView, context);
  }

  /**
   * @inheritDoc
   */
  public homePage(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.homePage, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.homePage, context);
  }

  /**
   * @inheritDoc
   */
  public propertyPage(propertyId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyPage, propertyId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public propertySerp(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.propertySerp, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.propertySerp, context);
  }

  /**
   * @inheritDoc
   */
  public propertyImpression(propertyId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyImpression, propertyId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public propertyLoad(propertyId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyLoad, propertyId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public propertyLeadClick(propertyId: number, localContext: StatsContextLocalInterface): void {
    // Generate event hash per lead medium
    const eventHash = [
      StatsData.PROPERTY,
      StatsEmitterEvent.propertyLeadClick,
      propertyId,
      localContext.lead.medium,
    ].join('-');

    // Emit entity event
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyLeadClick, propertyId, localContext, true, eventHash);
  }

  /**
   * @inheritDoc
   */
  public propertyLeadSend(propertyId: number, localContext: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyLeadSend, propertyId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public propertyClick(propertyId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyClick, propertyId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public propertySave(propertyId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertySave, propertyId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public propertyUnsave(propertyId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyUnsave, propertyId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public propertyImageBrowse(propertyId: number, localContext: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyImageBrowse, propertyId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public agentHome(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.agentHome, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.agentHome, context);
  }

  /**
   * @inheritDoc
   */
  public agentPage(agentId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.AGENT, StatsEmitterEvent.agentPage, agentId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public agentSerp(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.agentSerp, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.agentSerp, context);
  }

  /**
   * @inheritDoc
   */
  public agentImpression(agentId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.AGENT, StatsEmitterEvent.agentImpression, agentId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public agentLoad(agentId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.AGENT, StatsEmitterEvent.agentLoad, agentId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public agentLeadClick(agentId: number, localContext: StatsContextLocalInterface): void {
    // Generate event hash per lead medium
    const eventHash = [StatsData.AGENT, StatsEmitterEvent.agentLeadClick, agentId, localContext.lead.medium].join('-');

    // Emit entity event
    this.emitEntity(StatsData.AGENT, StatsEmitterEvent.agentLeadClick, agentId, localContext, true, eventHash);
  }

  /**
   * @inheritDoc
   */
  public agentLeadSend(agentId: number, localContext: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.AGENT, StatsEmitterEvent.agentLeadSend, agentId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public agentClick(agentId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.AGENT, StatsEmitterEvent.agentClick, agentId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public brokerHome(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.brokerHome, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.brokerHome, context);
  }

  /**
   * @inheritDoc
   */
  public brokerPage(brokerId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.BROKER, StatsEmitterEvent.brokerPage, brokerId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public brokerSerp(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.brokerSerp, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.brokerSerp, context);
  }

  /**
   * @inheritDoc
   */
  public brokerImpression(brokerId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.BROKER, StatsEmitterEvent.brokerImpression, brokerId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public brokerLoad(brokerId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.BROKER, StatsEmitterEvent.brokerLoad, brokerId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public brokerLeadClick(brokerId: number, localContext: StatsContextLocalInterface): void {
    // Generate event hash per lead medium
    const eventHash = [StatsData.BROKER, StatsEmitterEvent.brokerLeadClick, brokerId, localContext.lead.medium].join(
      '-'
    );

    // Emit entity event
    this.emitEntity(StatsData.BROKER, StatsEmitterEvent.brokerLeadClick, brokerId, localContext, true, eventHash);
  }

  /**
   * @inheritDoc
   */
  public brokerLeadSend(brokerId: number, localContext: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.BROKER, StatsEmitterEvent.brokerLeadSend, brokerId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public brokerClick(brokerId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.BROKER, StatsEmitterEvent.brokerClick, brokerId, localContext, true);
  }

  /**
   * @inheritDoc
   */
  public userPage(localContext?: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.userPage, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.userPage, context);
  }

  /**
   * @inheritDoc
   */
  public userSignIn(userId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.USER, StatsEmitterEvent.userSignIn, userId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public userSignOut(userId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.USER, StatsEmitterEvent.userSignOut, userId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public userRegister(userId: number, localContext?: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.USER, StatsEmitterEvent.userRegister, userId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public managerClick(category: string, action: string): void {
    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.managerClick, { category, action });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.managerClick, category, action);
  }

  /**
   * @inheritDoc
   */
  public ctaClick(localContext: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.ctaClick, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.ctaClick, context);
  }

  /**
   * @inheritDoc
   */
  public errorPage(localContext: StatsContextLocalInterface): void {
    const context = this.statsContextService.getContext(localContext);

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.errorPage, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.errorPage, context);
  }

  /**
   * @inheritDoc
   */
  public myAccountPage(): void {
    const context = this.statsContextService.getContext();

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.myAccountPage, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.myAccountPage, context);
  }

  /**
   * @inheritDoc
   */
  public buyVsRentCalculatorPage(): void {
    const context = this.statsContextService.getContext();

    // Debugger
    this.statsDebuggerService.log(StatsPlatformEnum.all, StatsEmitterEvent.buyVsRentCalculatorPage, { context });

    // Emit event, no strategy needed
    this.getEventEmitter().emit(StatsEmitterEvent.buyVsRentCalculatorPage, context);
  }

  /**
   * @inheritDoc
   */
  public propertyReadmoreClick(propertyId: number): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyReadmoreClick, propertyId, {}, true);
  }

  /**
   * @inheritDoc
   */
  public propertyLeadClickWhatsapp(propertyId: number, localContext: StatsContextLocalInterface): void {
    // Generate event hash per lead medium
    const eventHash = [
      StatsData.PROPERTY,
      StatsEmitterEvent.propertyLeadClickWhatsapp,
      propertyId,
      localContext.lead.medium,
    ].join('-');

    // Emit entity event
    this.emitEntity(
      StatsData.PROPERTY,
      StatsEmitterEvent.propertyLeadClickWhatsapp,
      propertyId,
      localContext,
      true,
      eventHash
    );
  }

  /**
   * @inheritDoc
   */
  public propertyLeadSendWhatsapp(propertyId: number, localContext: StatsContextLocalInterface): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.propertyLeadSendWhatsapp, propertyId, localContext, false);
  }

  /**
   * @inheritDoc
   */
  public mortgageFinderWidgetImpression(propertyId: number): void {
    this.emitEntity(StatsData.PROPERTY, StatsEmitterEvent.mortgageFinderWidgetImpression, propertyId, undefined, true);
  }

  /**
   * Emit an "entity event" @eventName for the @entityName entity identified by @entityId
   *
   * @param entityName
   * @param eventName
   * @param entityId
   * @param localContext
   * @param emitOnce
   * @param eventHash - Optional event hash to identify the event
   */
  protected emitEntity(
    entityName: string,
    eventName: string,
    entityId: number,
    localContext?: StatsContextLocalInterface,
    emitOnce?: boolean,
    eventHash?: string
  ): void {
    // Check we can emit the event for this entity
    this.statsEmitterEntityStrategy
      .canEmit(entityName, eventName, entityId, emitOnce, eventHash)
      .then((entity: StatsDataEntityInterface) => {
        // Get context
        const context = this.statsContextService.getContext(localContext);

        // Debugger
        this.statsDebuggerService.log(StatsPlatformEnum.all, eventName, {
          entity,
          context,
        });

        // Emit the event
        this.getEventEmitter().emit(eventName, entity, context);
      })
      .catch((e?: Error | string) => {
        if (e instanceof Error) {
          console.error(e);
        }
      });
  }
}
